"use client";

import { motion } from "motion/react";
import Image from "next/image";
import { BarChart3, Eye } from "lucide-react";

export type RoleBox = {
  label: string;
  logo: boolean;
  /** Position and size as percentages of the card. */
  left: number;
  top: number;
  width: number;
  height: number;
  /** Put the label under the box instead of above it. */
  tagBelow?: boolean;
};

export type RoleVisualData = {
  image: string;
  alt: string;
  boxes: RoleBox[];
  widget: { seconds: string; views: string; payout: string };
  /** Corner for the stats widget, chosen so it covers no logo or face. */
  widgetAt: "bl" | "br" | "tl" | "tr";
};

const WIDGET_POSITION: Record<RoleVisualData["widgetAt"], string> = {
  bl: "bottom-4 left-4",
  br: "bottom-4 right-4",
  tl: "top-4 left-4",
  tr: "top-4 right-4",
};

const Corners = ({ color }: { color: string }) => (
  <>
    <div className={`absolute -top-1 -left-1 w-2 h-2 border-t-2 border-l-2 ${color}`} />
    <div className={`absolute -top-1 -right-1 w-2 h-2 border-t-2 border-r-2 ${color}`} />
    <div className={`absolute -bottom-1 -left-1 w-2 h-2 border-b-2 border-l-2 ${color}`} />
    <div className={`absolute -bottom-1 -right-1 w-2 h-2 border-b-2 border-r-2 ${color}`} />
  </>
);

/**
 * The homepage hero's detection card, driven by data so each Request Access
 * tab can show its own photo. Same frame, scan line, blue item boxes, pink
 * logo boxes and stats widget; the widget is a size down to suit the panel.
 */
export function RoleDetectionCard({ image, alt, boxes, widget, widgetAt }: RoleVisualData) {
  const items = boxes.filter((b) => !b.logo);
  const isInsideItem = (b: RoleBox) =>
    items.some(
      (o) =>
        b.left >= o.left - 1 &&
        b.top >= o.top - 1 &&
        b.left + b.width <= o.left + o.width + 1 &&
        b.top + b.height <= o.top + o.height + 1
    );

  return (
    <div className="relative bg-white p-2 rounded-[2rem] shadow-2xl shadow-brand-900/15 border border-gray-100">
      <div className="relative rounded-[1.5rem] overflow-hidden bg-gray-900 aspect-[4/5] group">
        <Image
          src={image}
          alt={alt}
          fill
          sizes="420px"
          className="object-cover opacity-90 group-hover:opacity-75 transition-opacity duration-700"
        />

        {/* Scanning Overlay */}
        <motion.div
          className="absolute inset-0 bg-gradient-to-b from-transparent via-brand-400/20 to-transparent h-32 w-full"
          animate={{ y: ["-100%", "400%"] }}
          transition={{ repeat: Infinity, duration: 3, ease: "linear" }}
        />

        {boxes.map((b, i) => {
          const below = b.tagBelow || b.top < 6 || (b.logo && isInsideItem(b));
          const alignRight = b.left > 70;
          const border = b.logo ? "border-pink-400" : "border-blue-400";
          return (
            <motion.div
              key={`${b.label}-${i}`}
              initial={b.logo ? { opacity: 0, scale: 1.1 } : { opacity: 0 }}
              animate={b.logo ? { opacity: 1, scale: 1 } : { opacity: 1 }}
              transition={{ delay: (b.logo ? 1.4 : 0.7) + i * 0.2, duration: b.logo ? 0.35 : 0.4 }}
              className={`absolute border-2 rounded-sm ${border} ${b.logo ? "bg-pink-400/10" : "bg-blue-400/5"}`}
              style={{ left: `${b.left}%`, top: `${b.top}%`, width: `${b.width}%`, height: `${b.height}%` }}
            >
              <div
                className={`absolute ${below ? "-bottom-5 rounded-b-sm" : "-top-5 rounded-t-sm"} ${
                  alignRight ? "right-[-2px]" : "left-[-2px]"
                } ${b.logo ? "bg-pink-500" : "bg-blue-500"} text-white text-[9px] font-mono px-1.5 py-0.5 font-bold tracking-wider whitespace-nowrap`}
              >
                {b.label}
              </div>
              <Corners color={border} />
            </motion.div>
          );
        })}

        {/* Floating Stats Widget */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 2.2, duration: 0.5 }}
          className={`absolute ${WIDGET_POSITION[widgetAt]} glass-dark rounded-xl p-3 shadow-2xl w-40`}
        >
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-white/70 text-[10px] font-mono uppercase tracking-wider">Exposure time</span>
            <BarChart3 size={12} className="text-brand-400" />
          </div>
          <div className="flex items-baseline gap-1">
            <span className="text-xl font-display font-bold text-white">{widget.seconds}</span>
            <span className="text-white/50 text-xs">sec</span>
          </div>
          <div className="w-full bg-white/10 rounded-full h-1 mt-1.5 overflow-hidden">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: "65%" }}
              transition={{ delay: 2.5, duration: 1, ease: "easeOut" }}
              className="bg-brand-400 h-full rounded-full"
            />
          </div>
          <div className="mt-2 pt-2 border-t border-white/10 flex justify-between items-center">
            <span className="text-white/70 text-[10px] flex items-center gap-1">
              <Eye size={9} />
              Views
            </span>
            <span className="text-white font-mono font-bold text-xs">{widget.views}</span>
          </div>
          <div className="mt-1 flex justify-between items-center">
            <span className="text-white/70 text-[10px]">Est. payout</span>
            <span className="text-brand-400 font-mono font-bold text-xs">{widget.payout}</span>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
